import { tryCatchSync } from '@/try-catch';
import { SSE, type SSEvent } from 'sse.js';
import type { QueryClient } from '@tanstack/react-query';
import type { DocumentType } from '@/features/documents/constants';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { stream?: QueryStream; streamEnabled?: boolean };
  }
}

export const isEmptySnapshotRow = (row: { id?: unknown; status?: unknown }) =>
  row.id === null && row.status === null;

export type StreamRowPayload<TStatus> =
  | { rows: unknown }
  | { id?: string | null; status: TStatus | null; progress?: number | null };

export const parseStreamRow = <TStatus>(
  payload: StreamRowPayload<TStatus>,
): 'refetch' | { rowId: string; status: TStatus; progress: number } | null => {
  if ('rows' in payload) return 'refetch';

  const { id, status } = payload;
  if (!id || status == null) return 'refetch';
  const progress = Number(payload.progress);
  if (!Number.isFinite(progress)) return null;

  return { rowId: id, status, progress };
};

export type QueryStream = {
  topic: string;
  type: DocumentType;
  resourceId: string;
  versioned?: boolean;
  isEnabled?: (data: unknown) => boolean;
  isActive: (data: unknown) => boolean;
  onReconnect?: (queryClient: QueryClient) => void;
  onStatusUpdate: (payload: never, queryClient: QueryClient) => void;
};

export const queryStream = <TData, TPayload extends object>(
  stream: Omit<QueryStream, 'isEnabled' | 'isActive' | 'onStatusUpdate'> & {
    isEnabled?: (data: TData | undefined) => boolean;
    isActive: (data: TData | undefined) => boolean;
    onStatusUpdate: (payload: TPayload, queryClient: QueryClient) => void;
  },
) => stream as QueryStream;

const ACTIVE_RECONNECT_DELAY_MS = 3_000;
const IDLE_RECONNECT_DELAY_MS = 60_000;

export type StreamEnvelope = { key?: unknown; payload?: unknown };

const parseStreamEnvelope = (event: SSEvent): StreamEnvelope | null => {
  if (!event.data) return null;

  const envelope = tryCatchSync((): unknown => JSON.parse(String(event.data))).data;
  return envelope && typeof envelope === 'object' ? envelope : null;
};

type StreamSubscriber = {
  onStatusUpdate: (envelope: StreamEnvelope) => void;
  onReconnect?: () => void;
  active: boolean;
};

type StreamConnection = {
  subscribers: Set<StreamSubscriber>;
  connect: () => Promise<SSE>;
  source: SSE | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectingWhileIdle: boolean;
};

const streamConnections = new Map<string, StreamConnection>();

const openStream = (key: string, connection: StreamConnection, reconnecting = false) => {
  connection
    .connect()
    .then((source) => {
      if (streamConnections.get(key) !== connection) return source.close();

      connection.source = source;
      source.addEventListener('status_update', (event: SSEvent) => {
        if (connection.source !== source) return;
        const envelope = parseStreamEnvelope(event);
        if (!envelope) return;
        connection.subscribers.forEach((subscriber) => subscriber.onStatusUpdate(envelope));
      });
      source.addEventListener('open', (event: SSEvent) => {
        if (connection.source !== source) return;
        if (reconnecting && event.responseCode === 200)
          connection.subscribers.forEach((subscriber) => subscriber.onReconnect?.());
      });
      source.addEventListener('readystatechange', () => {
        if (connection.source !== source) return;
        if (source.readyState === SSE.CLOSED) scheduleStreamReconnect(key, connection);
      });
      source.stream();
    })
    .catch(() => scheduleStreamReconnect(key, connection));
};

const scheduleStreamReconnect = (key: string, connection: StreamConnection) => {
  if (streamConnections.get(key) !== connection || connection.reconnectTimer) return;

  const { source } = connection;
  connection.source = null;
  connection.reconnectingWhileIdle = [...connection.subscribers].every((s) => !s.active);
  connection.reconnectTimer = setTimeout(
    () => {
      connection.reconnectTimer = null;
      openStream(key, connection, true);
    },
    connection.reconnectingWhileIdle ? IDLE_RECONNECT_DELAY_MS : ACTIVE_RECONNECT_DELAY_MS,
  );
  source?.close();
};

const wakeStream = (key: string, connection: StreamConnection) => {
  if (!connection.reconnectTimer || !connection.reconnectingWhileIdle) return;

  clearTimeout(connection.reconnectTimer);
  connection.reconnectTimer = null;
  return openStream(key, connection, true);
};

export const subscribeToStream = (
  key: string,
  connect: () => Promise<SSE>,
  subscriber: StreamSubscriber,
) => {
  let connection = streamConnections.get(key);
  if (connection) {
    connection.subscribers.add(subscriber);
    if (subscriber.active) wakeStream(key, connection);
  } else {
    connection = {
      subscribers: new Set([subscriber]),
      connect,
      source: null,
      reconnectTimer: null,
      reconnectingWhileIdle: false,
    };
    streamConnections.set(key, connection);
    openStream(key, connection);
  }

  return {
    setActive: (active: boolean) => {
      subscriber.active = active;
      if (active) wakeStream(key, connection);
    },
    unsubscribe: () => {
      if (!connection.subscribers.delete(subscriber)) return;
      if (connection.subscribers.size > 0) return;

      streamConnections.delete(key);
      if (connection.reconnectTimer) clearTimeout(connection.reconnectTimer);
      connection.source?.close();
      connection.source = null;
    },
  };
};
